import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChartBarIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import DateRangePicker from "../../components/reports/DateRangePicker";
import SummaryStrip from "../../components/reports/SummaryStrip";
import RevenueTab from "../../components/reports/RevenueTab";
import OperationsTab from "../../components/reports/OperationsTab";
import FleetTab from "../../components/reports/FleetTab";
import TeamTab from "../../components/reports/TeamTab";
import { presetRange } from "../../lib/reports";

const TABS = [
  { key: "revenue", label: "Revenue" },
  { key: "operations", label: "Operations" },
  { key: "fleet", label: "Fleet Health" },
  { key: "team", label: "Team" },
];

export default function ReportsPage() {
  const [search, setSearch] = useSearchParams();

  // ----- range from URL params, defaulting to "last_30" -----
  const initial = useMemo(() => {
    const fromParam = search.get("from");
    const toParam = search.get("to");
    if (fromParam && toParam) {
      return { from: fromParam, to: toParam, preset: "custom" };
    }
    const r = presetRange("last_30");
    return { from: r.from, to: r.to, preset: "last_30" };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [range, setRange] = useState(initial);

  // tab from URL params, default revenue
  const initialTab = search.get("tab") || "revenue";
  const [tab, setTab] = useState(
    TABS.some((t) => t.key === initialTab) ? initialTab : "revenue"
  );

  // Sync state ↔ URL.
  useEffect(() => {
    const next = new URLSearchParams(search);
    next.set("from", range.from);
    next.set("to", range.to);
    next.set("tab", tab);
    setSearch(next, { replace: true });
  }, [range.from, range.to, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const params = { date_from: range.from, date_to: range.to };

  return (
    <Layout title="Reports" subtitle="Owner KPI dashboard">
      <EntityHeader
        title="Owner KPI dashboard"
        breadcrumb={[{ label: "Reports" }]}
        meta={
          <>
            <span className="inline-flex items-center gap-1.5">
              <ChartBarIcon size={12} weight="duotone" />
              {range.from} → {range.to}
            </span>
          </>
        }
        actions={
          <DateRangePicker value={range} onChange={(r) => setRange(r)} />
        }
      />

      <div className="mb-6">
        <SummaryStrip params={params} />
      </div>

      {/* Tab bar */}
      <div
        data-testid="reports-tabs"
        className="mb-4 flex flex-wrap gap-1 border-b border-slate-200"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`reports-tab-${t.key}`}
            className={`relative px-4 py-2 text-sm font-semibold transition ${
              tab === t.key
                ? "text-slate-900"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
            {tab === t.key && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 bg-amber-500" />
            )}
          </button>
        ))}
      </div>

      {/* Active tab body */}
      <div data-testid="reports-tab-body">
        {tab === "revenue" && <RevenueTab params={params} />}
        {tab === "operations" && <OperationsTab params={params} />}
        {tab === "fleet" && <FleetTab params={params} />}
        {tab === "team" && <TeamTab params={params} />}
      </div>
    </Layout>
  );
}
